import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { List, X, FacebookLogo, XLogo, LinkedinLogo } from "@phosphor-icons/react";
import { useGetInvolvedModal } from "@/contexts/GetInvolvedModalContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";

const LOGO_URL = "/kybia-logo.png";

export const SOCIAL_LINKS = [
  { Icon: FacebookLogo, href: "https://www.facebook.com/profile.php?id=61590691922004", label: "Facebook" },
  { Icon: XLogo, href: "https://x.com/KYBIAOrg", label: "X" },
  { Icon: LinkedinLogo, href: "https://www.linkedin.com/company/kybia", label: "LinkedIn" },
];

export default function Navbar() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { openModal } = useGetInvolvedModal();
  const { t } = useLanguage();

  const navLinks = [
    { label: t("nav_home"), path: "/" },
    { label: t("nav_about"), path: "/about" },
    { label: t("nav_research"), path: "/research" },
    { label: t("nav_news"), path: "/news" },
    { label: t("nav_reports"), path: "/reports" },
    { label: t("nav_petition"), path: "/petition" },
    { label: t("nav_contact"), path: "/contact" },
  ];

  const fontStack = "'IBM Plex Sans', 'Noto Sans Kannada', sans-serif";

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-50 border-b"
      style={{
        backgroundColor: 'rgba(247, 245, 240, 0.85)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderColor: 'rgba(26, 92, 55, 0.1)',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3">
          <img
            src={LOGO_URL}
            alt="KYBIA Logo"
            className="h-10 w-10 md:h-12 md:w-12 object-contain"
          />
          <span
            className="font-heading text-xl md:text-2xl font-semibold tracking-tight"
            style={{ color: '#1A5C37' }}
          >
            KYBIA
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6 lg:gap-8" data-testid="desktop-nav">
          {navLinks.map((link, idx) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${idx}`}
                className="text-sm font-medium transition-colors duration-200 pb-1"
                style={{
                  color: isActive ? '#1A5C37' : '#4A5450',
                  borderBottom: isActive ? '2px solid #1A5C37' : '2px solid transparent',
                  fontFamily: fontStack,
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.target.style.color = '#1A5C37';
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.target.style.color = '#4A5450';
                }}
              >
                {link.label}
              </Link>
            );
          })}
          <button
            onClick={openModal}
            data-testid="nav-volunteer-btn"
            className="px-5 py-2 rounded-full text-sm font-medium transition-colors duration-200"
            style={{
              backgroundColor: '#B85C38',
              color: '#F7F5F0',
              fontFamily: fontStack,
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#964B2E'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#B85C38'}
          >
            {t("nav_get_involved")}
          </button>
          <LanguageSwitcher />
          <div className="flex items-center gap-2 pl-2 border-l" style={{ borderColor: 'rgba(26,92,55,0.15)' }}>
            {SOCIAL_LINKS.map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`nav-social-${label.toLowerCase()}`}
                className="p-1.5 rounded-full transition-colors"
                style={{ color: '#1A5C37' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(26,92,55,0.08)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <Icon size={18} weight="fill" />
              </a>
            ))}
          </div>
        </nav>

        {/* Mobile Menu Toggle */}
        <button
          data-testid="mobile-menu-btn"
          className="md:hidden p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{ color: '#1A5C37' }}
        >
          {mobileOpen ? <X size={24} weight="bold" /> : <List size={24} weight="bold" />}
        </button>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <nav
          data-testid="mobile-nav"
          className="md:hidden border-t px-6 py-4 space-y-3"
          style={{
            backgroundColor: '#F7F5F0',
            borderColor: 'rgba(26, 92, 55, 0.1)',
          }}
        >
          {navLinks.map((link, idx) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-link-${idx}`}
                className="block text-base font-medium py-2"
                style={{
                  color: isActive ? '#1A5C37' : '#4A5450',
                  fontFamily: fontStack,
                }}
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </Link>
            );
          })}
          <button
            data-testid="mobile-volunteer-btn"
            onClick={() => { setMobileOpen(false); openModal(); }}
            className="w-full text-center px-5 py-2.5 rounded-full text-sm font-medium mt-2"
            style={{ backgroundColor: '#B85C38', color: '#F7F5F0', fontFamily: fontStack }}
          >
            {t("nav_get_involved")}
          </button>
          <div className="flex justify-center pt-2">
            <LanguageSwitcher />
          </div>
          <div className="flex items-center justify-center gap-4 pt-3">
            {SOCIAL_LINKS.map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`mobile-social-${label.toLowerCase()}`}
                className="p-2 rounded-full"
                style={{ color: '#1A5C37' }}
              >
                <Icon size={22} weight="fill" />
              </a>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}
